import { ReactElement, useRef, useState } from 'react';
// Design systems
import { Font } from 'src/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { LanguageSelector } from '../../language-selector/LanguageSelector';
import isEmpty from 'lodash.isempty';
// Features
import MegaNavDesktop from '@utmb/features/mega-nav/MegaNavDesktop';
import { LiveBanners } from '@utmb/features/live-banners/LiveBanners';
import { MyAccountDropdown } from '@utmb/features/layout/my-account-dropdown/MyAccountDropdown';
// Context
import { useGlobal } from '@utmb/contexts/GlobalContext';
import { useIsCaresTenant } from '@utmb/contexts/TenancyContext';
// Interfaces
import { ColorTypesEnum } from '@utmb/types/colors';
import { EventView } from '@utmb/types/index';
import { IconsEnum } from '@utmb/types/icons';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
// Styles
import styles from './top-bar.module.scss';
import { useRouter } from 'next/router';
import { MyParameters } from '../../my-parameters/MyParameters';
import { useUser } from '@utmb/contexts/AuthContext';
import { MyUtmbLabel } from '@utmb/features/my-utmb/MyUtmbLabel';
import { UtmbLink } from '@utmb/design-system/utmb-link/UtmbLink';

interface TopBarProps {
    eventName: string;
    eventsTopBar: EventView[];
    className?: string;
    languages: string[];
    translationSlugs?: {
        [key: string]: string;
    };
}

export const TopBar = (props: TopBarProps): ReactElement => {
    const { eventsTopBar, languages, translationSlugs } = props;
    const { me } = useUser();
    const isCares = useIsCaresTenant();

    const refMegaNav = useRef<HTMLDivElement>(null);
    const {
        global: {
            event: { isWS },
        },
    } = useGlobal();

    const { locale, defaultLocale } = useRouter();

    const [isMyAccountDropdownShow, myAccountDropdownShow] = useState(false);
    const [isMegaNavOpen, setIsMegaNavOpen] = useState(false);

    function handleMyAccountClick(e: any): boolean {
        if (e.type === 'keydown' && e.keyCode !== 13) {
            return false;
        }

        myAccountDropdownShow(!isMyAccountDropdownShow);

        return true;
    }

    function handleMegaNavToggle(e: any): boolean {
        if (typeof e !== 'boolean' && e.type === 'keydown' && e.keyCode !== 13) {
            return false;
        }

        const currentIsMegaNavOpen = !isMegaNavOpen;

        setIsMegaNavOpen(currentIsMegaNavOpen);

        if (refMegaNav.current) {
            if (currentIsMegaNavOpen) {
                disableBodyScroll(refMegaNav.current);
            } else {
                enableBodyScroll(refMegaNav.current);
            }
        }

        return true;
    }

    return (
        <div className={`row ${styles.top_nav_wrapper}`}>
            <div>
                <div className={styles.left_content_wrapper}>
                    <UtmbLink isWS={isWS} defaultLocale={defaultLocale} locale={locale} />
                    {!isCares && <div className={styles.separator} />}
                    {/* Race Selector (not on UTMB Cares) */}
                    {!isCares && !isEmpty(eventsTopBar) && (
                        <>
                            <div role="button" tabIndex={0} className={styles.mega_nav_link} onClick={handleMegaNavToggle} onKeyDown={handleMegaNavToggle}>
                                <Font.FuturaMedium mobile="13" as="span">
                                    UTMB World Series Events
                                </Font.FuturaMedium>
                                <Icon variant={IconsEnum.CHEVRON_RIGHT} />
                            </div>
                            <MegaNavDesktop ref={refMegaNav} isOpen={isMegaNavOpen} eventsTopBar={eventsTopBar} handleClose={handleMegaNavToggle} />
                        </>
                    )}
                </div>
            </div>
            <div>
                <LiveBanners />
            </div>
            <div>
                <div className={styles.right_content_wrapper}>
                    {!isCares && <MyParameters className={styles.selector} />}
                    <LanguageSelector className={styles.selector} languages={languages} translationSlugs={translationSlugs} />

                    <div
                        role="button"
                        tabIndex={0}
                        onClick={handleMyAccountClick}
                        onKeyDown={handleMyAccountClick}
                        className={[styles.topNav_dashboard, me?.loyalty ? styles.topNav_dashboard_level : null].filter(Boolean).join(' ')}
                    >
                        <MyUtmbLabel forcePlus={false} />
                        <Icon variant={IconsEnum.USER} color={getColorStyle(ColorTypesEnum.WS_PRIMARY)} />
                        <Icon clss={styles.svg_chevron} variant={IconsEnum.CHEVRON_RIGHT} color={getColorStyle(ColorTypesEnum.WS_PRIMARY)} />
                    </div>
                    <MyAccountDropdown isOpen={isMyAccountDropdownShow} />
                </div>
            </div>
        </div>
    );
};
